/**
 * EXEMPLO DE AUTHCONTEXT MIGRADO PARA USAR A API
 * 
 * Este arquivo mostra como o AuthContext deve ficar após a migração.
 * Para usar:
 * 1. Renomeie AuthContext.tsx para AuthContext.OLD.tsx (backup)
 * 2. Renomeie este arquivo para AuthContext.tsx
 * 3. Teste o login/logout
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import apiService from '../services/apiService';
import PasswordResetModal from '../components/auth/PasswordResetModal';

import { 
  AuthState, 
  AuthContextType, 
  InternalLoginCredentials, 
  InternalUser, 
  InternalSession,
  InternalAuthResponse,
  UserType,
  AuthUser
} from '../types';

// Chave para sessionStorage
const SESSION_STORAGE_KEY = 'sdr_odonto_session';

// Interface para dados da sessão
interface SessionData {
  userType: UserType;
  clinic_id: number | null;
  user: SupabaseUser | InternalUser;
  session: Session | InternalSession;
  token?: string;
}

// Funções utilitárias para sessionStorage
const sessionUtils = {
  save: (data: SessionData) => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Erro ao salvar sessão:', error);
    }
  },

  get: (): SessionData | null => {
    try {
      const data = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Erro ao recuperar sessão:', error);
      return null;
    }
  },

  clear: () => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('Erro ao limpar sessão:', error);
    }
  }
};

// Criando o contexto de autenticação
const AuthContext = createContext<AuthContextType>({
  authState: { user: null, userType: null, session: null, clinic_id: null, loading: true, error: null },
  signIn: async () => ({ success: false, error: 'Context not initialized' }),
  signInOwner: async () => ({ success: false, error: 'Context not initialized' }),
  signInInternal: async () => ({ success: false, error: 'Context not initialized' }),
  signOut: async () => {},
  isOwner: () => false,
  isInternal: () => false,
  getCurrentClinicId: () => null,
  showPasswordResetModal: () => {},
});

// Hook personalizado para utilizar o contexto de autenticação
export const useAuth = () => useContext(AuthContext);

// Provedor de autenticação
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    userType: null,
    session: null,
    clinic_id: null,
    loading: true,
    error: null,
  });

  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Verificar sessão ao carregar
  useEffect(() => {
    const checkUser = async () => {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));
      
      try {
        // Tentar restaurar sessão via API
        const response = await apiService.getSession();
        
        if (response.sucesso && (response as any).dados?.usuario) {
          const userData = (response as any).dados.usuario;
          
          setAuthState({
            user: userData,
            userType: 'internal',
            session: null,
            clinic_id: userData.internalUser?.clinic_id || null,
            loading: false,
            error: null,
          });
          
          // Salvar no sessionStorage
          sessionUtils.save({
            userType: 'internal',
            clinic_id: userData.internalUser?.clinic_id || null,
            user: userData,
            session: null as any,
          });
          return; // Sessão encontrada, sair
        }
      } catch (error: any) {
        // Sessão inválida é comportamento esperado quando não logado - não logar
      }
      
      // Se não há sessão via API, apenas marcar como não autenticado
      setAuthState((prev) => ({ ...prev, loading: false }));
    };

    checkUser();
  }, []);

  // Login de usuário interno (via API)
  const signInInternal = async ({ email, password }: { email: string; password: string }): Promise<InternalAuthResponse> => {
    try {
      const response = await apiService.login(email, password);
      
      if (!response.sucesso || !(response as any).usuario) {
        const errorMsg = response.mensagem || 'Erro ao fazer login';
        setAuthState((prev) => ({ ...prev, error: errorMsg }));
        return { success: false, error: errorMsg };
      }

      const userData = (response as any).usuario;
      
      setAuthState({
        user: userData,
        userType: 'internal',
        session: null as any,
        clinic_id: userData.internalUser?.clinic_id || null,
        loading: false,
        error: null,
      });

      sessionUtils.save({
        userType: 'internal',
        clinic_id: userData.internalUser?.clinic_id || null,
        user: userData,
        session: null as any,
      });

      return { success: true };
    } catch (error: any) {
      const errorMsg = error.message || 'Erro ao fazer login';
      setAuthState((prev) => ({ ...prev, error: errorMsg }));
      return { success: false, error: errorMsg };
    }
  };

  // Login de proprietário (via API - mesmo endpoint de usuários internos)
  const signInOwner = async (email: string, password: string) => {
    return signInInternal({ email, password });
  };

  // signIn genérico (redireciona para signInInternal)
  const signIn = async (email: string, password: string) => {
    return signInInternal({ email, password });
  };

  // Logout
  const signOut = async () => {
    try {
      // Logout via API (para todos os tipos de usuário)
      await apiService.logout();
      
      // Limpar estado
      setAuthState({
        user: null,
        userType: null,
        session: null,
        clinic_id: null,
        loading: false,
        error: null,
      });
      
      sessionUtils.clear();
      
    } catch (error: any) {
      console.error('Erro ao fazer logout:', error);
      
      // Mesmo com erro, limpar estado local
      setAuthState({
        user: null,
        userType: null,
        session: null,
        clinic_id: null,
        loading: false,
        error: null,
      });
      
      sessionUtils.clear();
    }
  };

  // Verificar se é proprietário
  const isOwner = (): boolean => {
    return authState.userType === 'owner';
  };

  // Verificar se é usuário interno
  const isInternal = (): boolean => {
    return authState.userType === 'internal';
  };

  // Obter ID da clínica atual
  const getCurrentClinicId = (): number | null => {
    return authState.clinic_id;
  };

  // Mostrar modal de reset de senha
  const showPasswordResetModal = () => {
    setShowPasswordReset(true);
  };

  const value: AuthContextType = {
    authState,
    signIn,
    signInOwner,
    signInInternal,
    signOut,
    isOwner,
    isInternal,
    getCurrentClinicId,
    showPasswordResetModal,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showPasswordReset && authState.user && authState.userType === 'internal' && (
        <PasswordResetModal
          isOpen={showPasswordReset}
          onClose={() => setShowPasswordReset(false)}
          currentUser={authState.user as InternalUser}
        />
      )}
    </AuthContext.Provider>
  );
};
